import { Layers, Shirt, Boxes, MapPin, Navigation } from "lucide-react";
import { useTranslation } from "react-i18next";
import Reveal from "./Reveal";

const LASER_IMG = "/laser-engraving-glass.jpg";
const TEXTILE_IMG = "/textile-emblem-shirt.jpg";
const ADDRESS_QUERY = "Calle Jose Valentin Zapata 9, 45370 Santa Cruz de la Zarza, Toledo, Spain";
const MAPS_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ADDRESS_QUERY)}`;
const MAPS_EMBED_URL = `https://www.google.com/maps?q=${encodeURIComponent(ADDRESS_QUERY)}&output=embed`;

const FEATURE_ICONS = [Layers, Shirt, Boxes, MapPin];

export default function About() {
  const { t } = useTranslation();
  const FEATURES = (t("about.features", { returnObjects: true }) || []).map((f, i) => ({ ...f, icon: FEATURE_ICONS[i] }));

  return (
    <section id="taller" data-testid="about-section" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.28em] text-[#F5CB5C] font-semibold">{t("about.kicker")}</div>
            <h2 className="mt-4 font-display text-white text-2xl sm:text-3xl lg:text-4xl leading-tight">
              {t("about.title1")} <span className="text-gold-gradient">{t("about.titleHighlight")}</span>
            </h2>
            <p className="mt-6 text-sm sm:text-base text-gray-400 leading-relaxed max-w-2xl">
              {t("about.paragraph")}
            </p>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <div data-testid={`about-feature-${i}`} className="flex gap-4 items-start">
                  <span className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-[#871430] to-[#540F20] border border-[#F5CB5C]/40 flex items-center justify-center">
                    <f.icon size={18} className="text-[#F5CB5C]" />
                  </span>
                  <div>
                    <h3 className="font-display text-white text-sm sm:text-base">{f.title}</h3>
                    <p className="mt-1.5 text-xs sm:text-sm text-gray-500 leading-relaxed">{f.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div data-testid="about-map-card" className="mt-8 rounded-2xl overflow-hidden glass-card border border-[#F5CB5C]/20">
              <div className="grid sm:grid-cols-2">
                <div className="p-6 flex flex-col justify-center">
                  <div className="flex items-center gap-2 text-[#F5CB5C] text-[10px] uppercase tracking-[0.24em] font-semibold">
                    <MapPin size={13} /> {t("about.mapKicker")}
                  </div>
                  <p className="mt-3 text-sm text-gray-400 leading-relaxed">{t("about.address")}</p>
                  <a
                    data-testid="about-directions-link"
                    href={MAPS_DIRECTIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#F5CB5C] hover:gap-3 transition-all duration-300"
                  >
                    <Navigation size={15} /> {t("about.mapCta")}
                  </a>
                </div>
                <iframe
                  data-testid="about-map-embed"
                  title={t("about.mapKicker")}
                  src={MAPS_EMBED_URL}
                  className="w-full h-[200px] sm:h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal className="lg:col-span-5 order-1 lg:order-2">
          <div className="relative">
            <div className="img-zoom rounded-3xl overflow-hidden glass-card relative">
              <img
                src={LASER_IMG}
                alt={t("about.laserImgAlt")}
                className="w-full h-[340px] sm:h-[420px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05070A]/85 via-transparent to-transparent pointer-events-none" />
            </div>
            <div className="absolute -bottom-8 -left-6 sm:-left-10 w-[46%] rounded-2xl overflow-hidden border-2 border-[#F5CB5C]/50 shadow-[0_10px_40px_rgba(0,0,0,0.55)] glass-card hidden sm:block">
              <img
                src={TEXTILE_IMG}
                alt={t("about.textileImgAlt")}
                className="w-full h-[140px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
